import { useState } from 'react';
import { PlanBadge } from '@/components/billing/PlanBadge';
import {
  CaretDown,
  Check,
  GearSix,
  List,
  Moon,
  Plus,
  SidebarSimple,
  SignOut,
  Sun,
  UserCircle,
  Users,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Tooltip } from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sidebar } from './Sidebar';
import { useApp } from '@/hooks/useApp';
import { useAuth, useWorkspace } from '@/hooks/useAuth';
import { authApi, workspacesApi } from '@/api/endpoints';
import { workspaceStore } from '@/auth/stores';

interface Props {
  isMobile: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  drawerOpen: boolean;
  onDrawerChange: (open: boolean) => void;
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export function Topbar({
  isMobile,
  collapsed,
  onToggleCollapse,
  drawerOpen,
  onDrawerChange,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { themeMode, toggleTheme } = useApp();
  const { user, signOut, refresh } = useAuth();
  const { workspaces, active, switchTo } = useWorkspace();

  const [createOpen, setCreateOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [creating, setCreating] = useState(false);

  const createWorkspace = async () => {
    if (!newWorkspaceName.trim()) return;

    setCreating(true);
    try {
      const workspace = await workspacesApi.create({
        name: newWorkspaceName.trim(),
      });
      workspaceStore.set(workspace.id);
      queryClient.clear();
      await refresh();
      setCreateOpen(false);
      setNewWorkspaceName('');
      navigate('/app');
      toast.success(`${workspace.name} is ready`);
    } catch (error: any) {
      toast.error(error?.message ?? 'Could not create the workspace');
    } finally {
      setCreating(false);
    }
  };

  const logout = async () => {
    authApi.logout().catch(() => undefined);
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-2 border-b border-border bg-card px-2 shadow-xs sm:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
          {isMobile ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDrawerChange(true)}
              aria-label="Open navigation"
            >
              <List size={18} />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <SidebarSimple size={18} />
            </Button>
          )}

          <div className="hidden shrink-0 sm:block">
            <PlanBadge />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 min-w-0 max-w-[260px] flex-1 gap-1.5 px-2 font-semibold sm:px-2.5"
              >
                <span className="min-w-0 truncate">
                  {active?.name ?? 'No workspace'}
                </span>
                <CaretDown size={12} className="opacity-60 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
              {workspaces.map((workspace) => (
                <DropdownMenuItem
                  key={workspace.id}
                  onClick={() => {
                    switchTo(workspace.id);
                    navigate('/app');
                  }}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2 truncate">
                    {workspace.id === active?.id ? (
                      <Check
                        size={14}
                        weight="bold"
                        className="text-primary shrink-0"
                      />
                    ) : (
                      <span className="w-3.5 inline-block" />
                    )}
                    <span className="truncate">{workspace.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground uppercase text-[10px]">
                    {workspace.role.toLowerCase()}
                  </span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/app/settings/workspace')}
              >
                <GearSix size={15} />
                <span>Workspace settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setCreateOpen(true)}>
                <Plus size={15} />
                <span>New workspace</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/app/posts/new')}
            className="gap-1 font-medium"
          >
            <Plus size={16} />
            {!isMobile && 'New post'}
          </Button>

          <Tooltip title={themeMode === 'light' ? 'Dark theme' : 'Light theme'}>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {themeMode === 'light' ? <Moon size={17} /> : <Sun size={17} />}
            </Button>
          </Tooltip>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer">
                <Avatar
                  size="sm"
                  className="bg-primary text-primary-foreground"
                >
                  <AvatarFallback>
                    {user ? initials(user.fullName || user.email) : '?'}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">
                {user?.email}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/app/settings/account')}
              >
                <UserCircle size={16} />
                <span>Account settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/app/settings/members')}
              >
                <Users size={16} />
                <span>Members</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} danger>
                <SignOut size={16} />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <Sheet open={drawerOpen} onOpenChange={onDrawerChange}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar onNavigate={() => onDrawerChange(false)} />
        </SheetContent>
      </Sheet>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New workspace</DialogTitle>
            <DialogDescription>
              A workspace has its own blogs, media and connected accounts.
              Nothing is shared between them.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Workspace name
            </label>
            <Input
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              placeholder="Acme Marketing"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') createWorkspace();
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={createWorkspace}
              loading={creating}
              disabled={!newWorkspaceName.trim()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
